import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, TrendingUp, Percent, Timer, Trophy, AlertTriangle, UserX, CarFront, KeyRound, ThumbsDown } from "lucide-react";
import { fmtEUR, fmtDate } from "@/lib/format";
import { KpiCard, Widget, Priorites, Ligne, DetailModal, chargerDetailKpi, libPeriode, TendanceSub } from "@/components/dashboards/commun";

export const DashChef = ({ d }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const k = d.kpis, w = d.widgets;
  const ouvrir = (cle) => chargerDetailKpi(cle, setDetail, { periode: d.periode });
  return (
    <div data-testid="dash-chef">
      {w.priorites && <Priorites items={w.priorites} />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {k.ventes_equipe && (
          <KpiCard icon={Car} couleur="#2F6FED" titre={libPeriode(d.periode, "Ventes équipe")} testId="kpi-ventes-equipe" onClick={() => ouvrir("ventes_equipe")}
            valeur={`${k.ventes_equipe.valeur} signée${k.ventes_equipe.valeur > 1 ? "s" : ""} / ${k.ventes_equipe.livraisons ?? 0} livrée${(k.ventes_equipe.livraisons ?? 0) > 1 ? "s" : ""}`}
            sub={<TendanceSub kpi={k.ventes_equipe} periode={d.periode} />}>
            <div className="h-1.5 bg-[#EEF1F6] rounded-full mt-1.5" title={`Commandes signées vs objectif : ${k.ventes_equipe.valeur} / ${k.ventes_equipe.objectif}`}>
              <div className="h-1.5 rounded-full bg-[#2F6FED]" style={{ width: `${Math.min(k.ventes_equipe.valeur / k.ventes_equipe.objectif * 100, 100)}%` }} />
            </div>
          </KpiCard>
        )}
        {k.pipeline_pondere && <KpiCard icon={TrendingUp} couleur="#16A34A" titre="Pipeline pondéré" valeur={fmtEUR(k.pipeline_pondere.valeur)} testId="kpi-pipeline-pondere" onClick={() => ouvrir("pipeline_pondere")} sub={<TendanceSub kpi={k.pipeline_pondere} periode={d.periode} />} />}
        {k.taux_conversion_equipe && <KpiCard icon={Percent} couleur="#7C5CE0" titre="Taux de conversion (90 j)" valeur={k.taux_conversion_equipe.valeur != null ? `${k.taux_conversion_equipe.valeur} %` : "—"} testId="kpi-conversion-equipe" onClick={() => ouvrir("taux_conversion_equipe")}
          sub={<TendanceSub kpi={k.taux_conversion_equipe} periode={d.periode}
            extra={<span className="text-[--idc-muted]" data-testid="conv-moyenne-vendeurs">· Moy. vendeurs : {k.taux_conversion_equipe.moyenne_vendeurs != null ? `${k.taux_conversion_equipe.moyenne_vendeurs} %` : "—"}</span>} />} />}
        {k.delai_closing && <KpiCard icon={Timer} couleur="#E8853D" titre="Délai moyen de closing" valeur={k.delai_closing.valeur != null ? `${k.delai_closing.valeur} j` : "—"} testId="kpi-delai-closing" onClick={() => ouvrir("delai_closing")} sub={<TendanceSub kpi={k.delai_closing} periode={d.periode} />} />}
      </div>
      <DetailModal detail={detail} onClose={() => setDetail(null)} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {w.classement_vendeurs && (
          <Widget titre="Classement des vendeurs" icon={Trophy} testId="widget-classement">
            <table className="w-full text-[12.5px]">
              <thead><tr className="text-[11px] text-[--idc-muted] text-left">
                <th className="pb-1.5">Vendeur</th><th className="pb-1.5 text-right">Cmdes</th>
                <th className="pb-1.5 text-right">Livr.</th>
                <th className="pb-1.5 text-right">Marge</th><th className="pb-1.5 text-right">Essais</th>
                <th className="pb-1.5 text-right">Conv.</th></tr></thead>
              <tbody>
                {w.classement_vendeurs.map((v, i) => (
                  <tr key={v.vendeur} className="border-t border-[--idc-border]" data-testid={`classement-${v.vendeur.replace(/ /g, "-")}`}>
                    <td className="py-1.5 font-semibold">{i === 0 && "🏆 "}{v.vendeur}</td>
                    <td className="py-1.5 text-right font-bold">{v.commandes ?? v.ventes ?? 0}</td>
                    <td className="py-1.5 text-right">{v.livraisons ?? 0}</td>
                    <td className="py-1.5 text-right">{fmtEUR(v.marge)}</td>
                    <td className="py-1.5 text-right">{v.essais}</td>
                    <td className="py-1.5 text-right">{v.conversion != null ? `${v.conversion} %` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Widget>
        )}
        {w.motifs_perte && (
          <Widget titre="Motifs de perte (top 5)" icon={ThumbsDown} testId="widget-motifs-perte"
            vide={w.motifs_perte.length === 0 && "Aucune affaire perdue sur la période."}>
            {(() => {
              const max = Math.max(...w.motifs_perte.map((m) => m.nb), 1);
              return w.motifs_perte.map((m, i) => (
                <button key={m.motif} onClick={() => navigate(`/pipeline?motif_perte=${encodeURIComponent(m.motif)}`)}
                  className="w-full text-left py-1.5 group" data-testid={`motif-perte-${i}`}>
                  <div className="flex items-center gap-2 text-[12.5px]">
                    <span className="flex-1 truncate font-medium group-hover:text-[#2F6FED] transition-colors">{m.motif}</span>
                    <span className="pill shrink-0" style={{ background: "#FEE2E2", color: "#B91C1C" }}>{m.nb}</span>
                    <span className="text-[12px] font-semibold text-[--idc-muted] w-24 text-right shrink-0 whitespace-nowrap">{fmtEUR(m.montant)}</span>
                  </div>
                  <div className="h-1.5 bg-[#EEF1F6] rounded-full mt-1">
                    <div className="h-1.5 rounded-full bg-[#DC2626]" style={{ width: `${(m.nb / max) * 100}%` }} />
                  </div>
                </button>
              ));
            })()}
          </Widget>
        )}
        {w.affaires_risque && (
          <Widget titre="Affaires à risque (> 7 j sans activité)" icon={AlertTriangle} testId="widget-affaires-risque" vide={w.affaires_risque.length === 0 && "Aucune affaire bloquée."}>
            {w.affaires_risque.map((o) => (
              <Ligne key={o.id} onClick={() => navigate(`/pipeline/opportunites/${o.id}`)} testId={`risque-${o.id}`}>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium truncate">{o.client} · {o.vendeur}</div>
                  <div className="text-[11.5px] text-[#B45309]">Dernière activité : {fmtDate(o.derniere_activite)} · {o.montant ? fmtEUR(o.montant) : ""}</div>
                </div>
              </Ligne>
            ))}
          </Widget>
        )}
        {w.non_affectees && (
          <Widget titre="Opportunités non affectées" icon={UserX} testId="widget-non-affectees" vide={w.non_affectees.length === 0 && "Toutes les opportunités sont affectées."}>
            {w.non_affectees.map((o) => (
              <Ligne key={o.id} onClick={() => navigate(`/pipeline/opportunites/${o.id}`)} testId={`nonaffectee-${o.id}`}>
                <span className="text-[13px] font-medium truncate">{o.client}</span>
                <span className="text-[12px] text-[--idc-muted] ml-auto shrink-0">{o.montant ? fmtEUR(o.montant) : ""}</span>
              </Ligne>
            ))}
          </Widget>
        )}
        {w.stock_age && (
          <Widget titre="Stock âgé à pousser (> 60 j)" icon={CarFront} testId="widget-stock-age" vide={w.stock_age.length === 0 && "Aucun VO âgé en stock."}>
            {w.stock_age.map((v) => (
              <Ligne key={v.id} onClick={() => navigate(`/stock-vehicules/${v.id}`)} testId={`stockage-${v.id}`}>
                <span className="text-[13px] font-medium truncate">{v.libelle}</span>
                <span className="pill shrink-0" style={{ background: "#FEE2E2", color: "#B91C1C" }}>{v.jours} j</span>
                <span className="text-[12px] text-[--idc-muted] ml-auto shrink-0">{v.prix ? fmtEUR(v.prix) : ""}</span>
              </Ligne>
            ))}
          </Widget>
        )}
        {w.essais_semaine && (
          <Widget titre="Essais de la semaine (équipe)" icon={KeyRound} testId="widget-essais-semaine" vide={w.essais_semaine.length === 0 && "Aucun essai planifié cette semaine."}>
            {w.essais_semaine.map((e) => (
              <Ligne key={e.id} onClick={() => e.opportunite_id ? navigate(`/pipeline/opportunites/${e.opportunite_id}`) : navigate("/agenda")} testId={`essaisem-${e.id}`}>
                <span className="font-mono text-[12px] text-[--idc-muted] shrink-0">{fmtDate(e.date)} {e.heure}</span>
                <span className="text-[13px] font-medium truncate">{e.client}</span>
                <span className="text-[12px] text-[--idc-muted] ml-auto shrink-0">{e.vendeur}</span>
              </Ligne>
            ))}
          </Widget>
        )}
      </div>
    </div>
  );
};
